import { useState, useCallback, useEffect } from 'react';
import api from '../services/api'; // Adjust path if api.js is elsewhere

export const useSummaryManagement = (conversationId, token) => {
  const [therapistSummary, setTherapistSummary] = useState(null);
  const [clientSummary, setClientSummary] = useState(null);
  const [reflectionPrompts, setReflectionPrompts] = useState([]);
  const [summariesLoaded, setSummariesLoaded] = useState(false); // New state to track if summaries have been loaded
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [success, setSuccess] = useState(''); // For "Summaries generated/refreshed successfully."

  const [isEditingClientSummary, setIsEditingClientSummary] = useState(false);
  const [editableClientSummaryText, setEditableClientSummaryText] = useState('');
  const [clientSummaryEditError, setClientSummaryEditError] = useState('');
  const [clientSummaryEditLoading, setClientSummaryEditLoading] = useState(false);

  // Function to load existing summaries
  const loadExistingSummaries = useCallback(async () => {
    if (!conversationId || !token) {
        // Clear summaries if conversationId or token is missing
        setTherapistSummary(null);
        setClientSummary(null);
        setReflectionPrompts([]);
        setSummariesLoaded(false);
        setSummaryError(''); // Clear any previous error
        return;
    }
    setSummaryLoading(true);
    setSummaryError('');
    try {
      console.log(`Hook: Attempting to load existing summaries for ${conversationId}`);
      const summaryData = await api.getTherapistSessionSummary(conversationId, token);
      if (summaryData) {
        const therapist = summaryData.therapistSummary || null;
        const client = summaryData.clientSummary || null;
        const prompts = summaryData.reflectionPrompts || [];

        setTherapistSummary(therapist);
        setClientSummary(client);
        setReflectionPrompts(prompts);

        // Set from the CONTENT, not from the envelope. A conversation that has
        // never been summarised still answers 200 with
        // {therapistSummary: null, clientSummary: null, reflectionPrompts: []},
        // so a truthy response does not mean a summary exists. Every consumer
        // reads this flag as "there is a summary": ConversationView passes it
        // straight through as `hasSummary`, which is why the sidebar offered
        // View Summary and Regenerate Summary for a conversation that had
        // neither, and SummaryPanel gates its "No summary generated yet" empty
        // state on it, which is why opening that panel showed a completely
        // blank slide-out instead (ST-20260922-0001). The empty state was
        // already built; it was simply unreachable.
        setSummariesLoaded(Boolean(therapist || client || prompts.length));

        console.log('Hook: Existing summaries loaded:', summaryData);
      } else {
        setTherapistSummary(null);
        setClientSummary(null);
        setReflectionPrompts([]);
        setSummariesLoaded(false); // No summaries found
      }
    } catch (err) {
      // If 404 (summaries not generated yet), it's not a critical error for this initial load.
      // Other errors should be displayed.
      if (err.message && !err.message.toLowerCase().includes('not found') && !err.message.toLowerCase().includes('not yet generated')) {
           setSummaryError('Failed to load existing summaries. ' + err.message);
      } else {
          console.log(`Hook: No existing summaries found for ${conversationId} or specific error: ${err.message}`);
          setTherapistSummary(null);
          setClientSummary(null);
          setReflectionPrompts([]);
          setSummariesLoaded(false); // Explicitly set to false if not found
      }
    } finally {
      setSummaryLoading(false);
    }
  }, [conversationId, token]);

  // Effect to load summaries when conversationId or token changes
  useEffect(() => {
    loadExistingSummaries();
  }, [loadExistingSummaries]);

  // Function to generate/regenerate summaries
  const handleGenerateSummary = useCallback(async () => {
    if (!conversationId || !token || summaryLoading) return;
    setSummaryLoading(true);
    setSummaryError('');
    setSuccess(''); // Clear previous success message
    // Optionally clear existing summaries here if we want "Regenerate" to feel like a fresh generation indication
    // setTherapistSummary(null);
    // setClientSummary(null);
    // setReflectionPrompts([]);
    // setSummariesLoaded(false);

    try {
      console.log(`Hook: Requesting generation of summaries for ${conversationId}`);
      const response = await api.generateSessionSummary(conversationId, token);
      setTherapistSummary(response.therapistSummary || null);
      setClientSummary(response.clientSummary || null);
      setReflectionPrompts(response.reflectionPrompts || []);
      setSummariesLoaded(true); // Mark as loaded after generation
      setSuccess('Summaries generated/refreshed successfully.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error("Hook: Error generating/fetching summary:", err);
      setSummaryError(err.message || 'Failed to generate/fetch summary.');
      setSummariesLoaded(false); // Ensure loaded is false on error
    } finally {
      setSummaryLoading(false);
    }
  }, [conversationId, token, summaryLoading]);

  // Handlers for client summary editing
  const handleStartEditClientSummary = useCallback(() => {
    if (clientSummary) {
      setEditableClientSummaryText(clientSummary);
      setIsEditingClientSummary(true);
      setClientSummaryEditError('');
    }
  }, [clientSummary]);

  const handleCancelEditClientSummary = useCallback(() => {
    setIsEditingClientSummary(false);
    setEditableClientSummaryText('');
    setClientSummaryEditError('');
  }, []);

  const handleSaveEditedClientSummary = useCallback(async () => {
    if (!conversationId || !token || !editableClientSummaryText.trim()) {
      setClientSummaryEditError('Summary text cannot be empty.');
      return;
    }
    setClientSummaryEditLoading(true);
    setClientSummaryEditError('');
    try {
      const response = await api.updateClientSummary(conversationId, editableClientSummaryText, token);
      setClientSummary(response.clientSummary); // Update local state with the saved summary
      setIsEditingClientSummary(false);
      setSuccess('Client summary updated successfully.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error("Hook: Error saving edited client summary:", err);
      setClientSummaryEditError(err.message || 'Failed to save edited summary.');
    } finally {
      setClientSummaryEditLoading(false);
    }
  }, [conversationId, token, editableClientSummaryText]);

  // Handler for the editable text field's onChange
  const handleEditableClientSummaryTextChange = useCallback((e) => {
    setEditableClientSummaryText(e.target.value);
  }, []);


  return {
    therapistSummary,
    clientSummary,
    reflectionPrompts,
    summariesLoaded,
    summaryLoading,
    summaryError,
    successMessage: success, // Renamed to avoid conflict if parent uses 'success' state
    isEditingClientSummary,
    editableClientSummaryText,
    clientSummaryEditError,
    clientSummaryEditLoading,
    loadExistingSummaries, // Expose if parent needs to trigger load manually
    handleGenerateSummary,
    handleStartEditClientSummary,
    handleCancelEditClientSummary,
    handleSaveEditedClientSummary,
    handleEditableClientSummaryTextChange,
    setSummaryError, // Allow parent to clear error if needed
    setSuccess, // Allow parent to clear success if needed
  };
};
